import { useEffect, useState } from "react";
import { CheckCircle2, Info, X } from "lucide-react";
import { subscribeBanner, dismissBanner } from "@/lib/toastBanner";

/**
 * <BannerHost /> — mounts once per layout. Renders a stack of dismissible
 * inline banners (success / info) at the top of the content area. Errors
 * still flow through sonner's <Toaster>.
 */
export default function BannerHost() {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    const unsub = subscribeBanner((evt) => {
      if (evt.kind === "add") {
        setBanners((prev) => [...prev, evt.banner]);
        if (evt.banner.ttl > 0) {
          setTimeout(() => {
            setBanners((prev) => prev.filter((b) => b.id !== evt.banner.id));
          }, evt.banner.ttl);
        }
      } else if (evt.kind === "dismiss") {
        setBanners((prev) => prev.filter((b) => b.id !== evt.id));
      }
    });
    return unsub;
  }, []);

  if (!banners.length) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="banner-host">
      {banners.map((b) => {
        const palette = b.type === "info"
          ? { bg: "#EFF6FF", border: "#BFDBFE", text: "#1E3A8A", Icon: Info }
          : { bg: "#ECFDF5", border: "#A7F3D0", text: "#065F46", Icon: CheckCircle2 };
        const { Icon } = palette;
        return (
          <div
            key={b.id}
            role="status"
            data-testid={`banner-${b.type}`}
            className="flex items-start gap-3 px-4 py-3 rounded-lg border text-sm font-body"
            style={{ backgroundColor: palette.bg, borderColor: palette.border, color: palette.text }}
          >
            <Icon className="h-4 w-4 shrink-0 mt-0.5" />
            <span className="flex-1">{b.message}</span>
            <button
              type="button"
              onClick={() => dismissBanner(b.id)}
              className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
              aria-label="Dismiss"
              data-testid={`banner-dismiss-${b.id}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
